import { FC, FocusEvent, useEffect, useState } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import FormField from '@app/shared/components/form-controls/form-field/FormField';
import { percentageValidator, getRequiredValidator } from '@core/validations/hook-forms/validators';
import { swapMaskForValueDisplay } from '@app/shared/components/grid-controls/grid-text-field/grid-cell-value-patterns';
import { Card } from '@app/shared/components/card/Card';
import styles from './deal-thesis-page.module.scss';
import { useAppDispatch, useAppSelector } from '@core/hooks/redux-hooks';
import { Navigate, useParams } from 'react-router-dom';
import * as projectActions from '@core/store/project-slice';
import * as capitalStructureActions from '@core/store/capital-structure-slice';
import {
  CaseDto,
  FinancialMetricDefinitionDto,
  HistoricForecastDto,
} from '@app/shared/models/contracts/project-dto';
import { FormSelect } from '@app/shared/components/form-controls/form-select/FormSelect';
import {
  cloneToMutableData,
  cloneForecast,
  enumKeyByValue,
  groupBy,
  sumBy,
  roundToFixedPoint,
} from '@app/shared/helpers';
import { percentageFieldFormattingProps } from '@app/shared/components/form-controls/form-field/form-field-patterns';
import BaseModal from '@app/shared/components/modal/BaseModal';
import Button from '@app/shared/components/button/Button';
import { numberValueFormatter, percentageValueFormatterProps } from '@app/shared/formatters';
import { NotificationMessage } from '@app/shared/components/notification-message/NotificationMessage';
import { formConfigBase } from '@app/shared/constants/form-config-base';
import { ButtonAppearance } from '@app/shared/components/button/button-enums';
import { SelectOption } from '@app/shared/models/option';
import { ModalSize } from '@app/shared/components/modal/base-modal-enums';
import { NotificationMessageType } from '@app/shared/components/notification-message/notification-message-enums';
import PwcNavigate from '@app/shared/routing/PwcNavigate';
import { usePwcNavigate } from '@core/hooks/routing-hooks';
import { EmptyValues } from '@app/shared/constants/empty-values';
import SvgTrash from '@app/shared/icons/Trash';
import useIsReadOnly from '@core/hooks/customUseIsReadOnly';
import {
  CalcMethod,
  EquityBridgeColumnRole,
  ReduceYearsType,
} from '@app/shared/models/contracts/enums/shared-enums';
import { RouteConstants } from '../../RouteConstants';
import {
  setDealThesisSelectedTab,
  setDealThesisShowHistoricYears,
} from '@core/store/ui-values-slice';
import { DealThesisCaseTable2 } from './deal-thesis-case-table-2/DealThesisCaseTable2';
import { useLocale } from '@app/core/hooks/useLocale';
import { getPwermValuationValueDriver } from '@app/shared/models/helpers/project-helpers';
import KeyFinancialMetrics from './deal-thesis-case-table-2/key-financial-metrics/KeyFinancialMetrics';
import { ReduceYearsModal } from '@app/shared/components/modal/reduce-years-modal/ReduceYearsModal';
import {
  getWholeYearsBetweenDates,
  reconcileForecastNetDebtValues,
} from './deal-thesis-utils/DealThesisHelpers';
import { FeatureFlags, getFeatureFlag } from '@app/core/feature-flags/feature-flags';
import {
  InstrumentDefinitionAndInitialValues,
  selectSlnAndPrefSharesInstrumentsIncludeInitialValues,
  selectCapitalStructure,
  selectCapitalStructures,
} from '@app/core/store/capital-structure-slice-selectors';
import { Tooltip, TooltipPlacement } from '@app/shared/components/tooltip/Tooltip';
import useInstrumentSort from '@app/shared/hooks/useInstrumentSort';

export interface KeyFinancialMetrics {
  metricId: string;
  narrative: string;
  forecast: Nullable<number>[];
}

export interface DealThesisNetDebtItemFormModel {
  netDebtItemId: string;
  narrative: string;
  historicalValue: Nullable<number>;
}

export interface DealThesisFormModel extends CaseDto {
  slnAndPrefSharesInstruments: InstrumentDefinitionAndInitialValues[];
  numberOfForecastYears: number;
  historicForecasts: HistoricForecastDto[];
  numberOfHistoricYears: number;
  historicalYear: string;
  valuationDateCashValue: Nullable<number>;
  exitValueDriverLabel: string;
  historicalExitValueDriver: Nullable<number>;
  keyFinancialMetrics: KeyFinancialMetrics[];
  activeMetric: string;
  netDebtItems: DealThesisNetDebtItemFormModel[];
}

export const DealThesisForm2 = () => {
  const PARENT_CLASSNAME = 'case-form-card';
  const { l } = useLocale();
  const isHistoricInputsEnabled = getFeatureFlag(FeatureFlags.DealThesisHistoricInputs);
  const dispatch = useAppDispatch();
  const { navigate, generatePath } = usePwcNavigate();
  const { caseId } = useParams();
  const { isPwermInputsValid, projectDraft: project } = useAppSelector((state) => state.project);
  const opmResultsInputSummary = useAppSelector(
    (state) => state.opmCalculation.riskFreeValues.inputSummary
  );
  const results = useAppSelector((state) => state.pwerm2Calculation.calculatedInputs);
  // adjust the coupon values to be displayed as percentages - this will not be required once initial coupons are moved into the Build Structure pages
  const slnAndPrefSharesInstruments = useAppSelector(
    selectSlnAndPrefSharesInstrumentsIncludeInitialValues
  ).map((ins) => {
    if (ins.initialValues?.coupon) {
      return {
        ...ins,
        initialValues: {
          ...ins.initialValues,
          coupon: {
            ...ins.initialValues.coupon,
            value: (ins.initialValues.coupon?.value ?? 0) * 100,
          },
        },
      };
    }

    return ins;
  });
  const sortedSlnAndPrefSharesInstruments = useInstrumentSort(slnAndPrefSharesInstruments);

  // caseId may be undefined momentarily when deleting a case as the project updates ahead of the navigation
  const caseIdIsInvalid = !project.pwermInput.cases.some((x) => x.caseId === caseId);
  const caseData = project.pwermInput.cases.find((caseItem) => caseItem.caseId === caseId);
  const historicData = cloneToMutableData(project.pwermInput.historicForecasts);
  const activeCase = caseData?.narrative;
  const [isDeleteCaseModalVisible, setIsDeleteCaseModalVisible] = useState(false);
  const [isForecastYearRemovalModalVisible, setIsForecastYearRemovalModalVisible] = useState(false);
  const [isHistoricForecastYearRemovalModalVisible, setIsHistoricForecastYearRemovalModalVisible] =
    useState(false);
  const isReadOnly = useIsReadOnly();
  const caseExists = project.pwermInput.cases.some((x) => x.caseId === caseId);
  const firstYearEndDate = caseData ? caseData.forecasts[0].forecastYear : '';
  const capitalStructures = useAppSelector(selectCapitalStructures);
  const capitalStructureKey = Object.keys(capitalStructures)[0];
  const capitalStructure = useAppSelector(selectCapitalStructure);

  const maximumHistoricYearsAllowed = getWholeYearsBetweenDates(
    firstYearEndDate,
    project.investmentDate
  );

  useEffect(() => {
    dispatch(setDealThesisSelectedTab(caseId));
  }, [caseId, dispatch]);

  useEffect(() => {
    if (!caseExists) {
      navigate('../');
    }
  }, [project.pwermInput.cases, caseExists, navigate]);

  const valuationValueDriver = getPwermValuationValueDriver(project);

  const bridgeNetDebtItems = project.equityBridge.netDebtItems;
  const valuationColumn = project.equityBridge.columns.find(
    (c) => c.role === EquityBridgeColumnRole.Valuation
  );

  const defaultFormValues: DealThesisFormModel = {
    caseId: caseId ?? '',
    narrative: caseData?.narrative ?? '',
    probability: caseData?.probability ?? 0,
    capitalStructureId: caseData?.capitalStructureId ?? '',
    eventSetId: caseData?.eventSetId ?? null,
    multiples: caseData?.multiples ?? [],
    forecasts: reconcileForecastNetDebtValues(caseData?.forecasts ?? [], bridgeNetDebtItems),
    numberOfForecastYears: caseData?.forecasts.length ?? 3,
    historicForecasts: historicData ?? [],
    numberOfHistoricYears: historicData.length ?? 0,
    historicalYear: project.pwermInput.valuationDateNarrative,
    keyFinancialMetrics: Object.entries(project.pwermInput.metricDefinitions).map(
      ([key, value]) => {
        const valuationDateMetrics = project.pwermInput.valuationDateMetrics[key];
        const forecast: Nullable<number>[] = caseData
          ? Object.values(caseData?.forecasts).map((forecast) => forecast.metricValues[key])
          : [];
        forecast.unshift(valuationDateMetrics);
        return {
          metricId: key,
          narrative: value.narrative,
          forecast: forecast,
        };
      }
    ),
    valuationDateCashValue: valuationColumn?.cashValue ?? null,
    exitValueDriverLabel:
      project.pwermInput.metricDefinitions[project.pwermInput.valueDriverActiveMetric].narrative,
    historicalExitValueDriver:
      valuationValueDriver.value || valuationValueDriver.value === 0
        ? valuationValueDriver.value
        : null,
    slnAndPrefSharesInstruments: sortedSlnAndPrefSharesInstruments ?? [],
    activeMetric: project.pwermInput.valueDriverActiveMetric,
    dividendSetId: caseData?.dividendSetId ?? null,
    netDebtItems: bridgeNetDebtItems.map((item) => ({
      netDebtItemId: item.netDebtItemId,
      narrative: item.narrative,
      historicalValue: valuationColumn?.netDebtValues[item.netDebtItemId] ?? null,
    })),
  };

  const formMethods = useForm<DealThesisFormModel>({
    ...formConfigBase,
    defaultValues: defaultFormValues,
  });

  const { append: appendForecastsField, remove: removeForecastsField } = useFieldArray({
    name: 'forecasts',
    control: formMethods.control,
  });

  const { append: appendHistoricForecastsField, remove: removeHistoricForecastsField } =
    useFieldArray({
      name: 'historicForecasts',
      control: formMethods.control,
    });

  const { handleSubmit, reset, getValues, trigger } = formMethods;

  const [countOfForecastYears, setCountOfForecastYears] = useState<number>(
    defaultFormValues.numberOfForecastYears
  );
  const [countOfHistoricYears, setCountOfHistoricYears] = useState<number>(
    defaultFormValues.numberOfHistoricYears
  );

  const numberOfForecastYearsOptions: SelectOption[] = [...Array(10).keys()].map((item) => ({
    viewValue: item + 1,
    value: item + 1,
  }));

  const numberOfHistoricYearsOptions: SelectOption[] = [
    ...Array((maximumHistoricYearsAllowed ?? 0) + 1).keys(),
  ].map((item) => ({
    viewValue: item,
    value: item,
  }));

  const caseProbabilitiesTotal = roundToFixedPoint(
    sumBy(project.pwermInput.cases, (caseItem) => Number(caseItem.probability))
  );

  const probabilitiesTotalWarningMessage =
    caseProbabilitiesTotal !== 100
      ? l('_AllProbabilitiesMustBe100Error', {
          type: l('_CaseLower'),
          amount: !Number.isNaN(caseProbabilitiesTotal)
            ? numberValueFormatter({
                value: caseProbabilitiesTotal!,
                ...percentageValueFormatterProps,
              })
            : EmptyValues.EnDash,
        })
      : undefined;

  const isStructureTheSame =
    Object.keys(groupBy(project.pwermInput.cases, (c) => c.forecasts.length)).length === 1 &&
    Object.keys(groupBy(project.pwermInput.cases, (c) => c.multiples.length)).length === 1;
  const caseStructuresDiffersWarningMessage = l('_NoOfForecastYearsExitMultiplesMismatchWarning');

  useEffect(() => {
    reset(defaultFormValues);
    setCountOfForecastYears(defaultFormValues.numberOfForecastYears);
    setCountOfHistoricYears(defaultFormValues.numberOfHistoricYears);
    trigger();
  }, [reset, caseId]); // eslint-disable-line react-hooks/exhaustive-deps

  // re-trigger validation because some validation relies on the updated project draft (Key Financial Metrics narrative uniqueness)
  useEffect(() => {
    trigger();
  }, [project]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleForecastYearQuantityChange = () => {
    if (Number(countOfForecastYears) > Number(getValues().numberOfForecastYears)) {
      setIsForecastYearRemovalModalVisible(true);
    } else if (Number(countOfForecastYears) < Number(getValues().numberOfForecastYears)) {
      addForecastYears();
      setCountOfForecastYears(getValues().numberOfForecastYears);
    }
  };

  const handleHistoricForecastYearQuantityChange = () => {
    if (Number(countOfHistoricYears) > Number(getValues().numberOfHistoricYears)) {
      setIsHistoricForecastYearRemovalModalVisible(true);
    } else if (Number(countOfHistoricYears) < Number(getValues().numberOfHistoricYears)) {
      addHistoricForecastYears();
      setCountOfHistoricYears(getValues().numberOfHistoricYears);
    }
  };

  const revertForecastYears = () => {
    setIsForecastYearRemovalModalVisible(false);
    formMethods.setValue('numberOfForecastYears', countOfForecastYears);
    submitData();
  };

  const removeForecastYears = () => {
    const forecasts = caseData?.forecasts ?? [];

    removeForecastsField(
      [
        ...Array(Number(countOfForecastYears) - Number(getValues().numberOfForecastYears)).keys(),
      ].map((index) => {
        return forecasts.length - index - 1;
      })
    );
    submitData();
    setIsForecastYearRemovalModalVisible(false);
    setCountOfForecastYears(getValues().numberOfForecastYears);
  };

  const addForecastYears = () => {
    const forecasts = caseData?.forecasts ?? [];
    const lastCaseItemForecast = forecasts?.[forecasts?.length - 1];
    [...Array(Number(getValues().numberOfForecastYears) - Number(countOfForecastYears)).keys()].map(
      (index) => {
        const clonedForecast = cloneForecast(lastCaseItemForecast);
        appendForecastsField({
          ...clonedForecast,
          forecastYear: new Date(clonedForecast.forecastYear).addYears(index + 1).toISODateString(),
        });
      }
    );
  };

  const revertHistoricForecastYears = () => {
    setIsHistoricForecastYearRemovalModalVisible(false);
    formMethods.setValue('numberOfHistoricYears', countOfHistoricYears);
    submitData();
  };

  const removeHistoricForecastYears = () => {
    const historicForecasts = formMethods.getValues().historicForecasts ?? [];
    removeHistoricForecastsField(
      [
        ...Array(Number(countOfHistoricYears) - Number(getValues().numberOfHistoricYears)).keys(),
      ].map((index) => {
        return historicForecasts.length - index - 1;
      })
    );
    submitData();
    setIsHistoricForecastYearRemovalModalVisible(false);
    setCountOfHistoricYears(getValues().numberOfHistoricYears);
  };

  const addHistoricForecastYears = () => {
    const historicForecasts = formMethods.getValues().historicForecasts ?? [];
    const firstYearEndDate = caseData ? caseData.forecasts[0].forecastYear : '';
    const lastCaseItemHistoricForecast = historicForecasts?.[historicForecasts?.length - 1];

    if (historicForecasts.length === 0) {
      [...Array(Number(getValues().numberOfHistoricYears)).keys()].map((index) => {
        appendHistoricForecastsField({
          ...historicData,
          date: new Date(firstYearEndDate).subtractYears(index + 1).toISODateString(),
          metricValues: {
            ...formMethods.getValues().keyFinancialMetrics.reduce((acc, metric) => {
              acc[metric.metricId] = null;
              return acc;
            }, {} as Record<string, Nullable<number>>),
          },
          historicNetDebt: null,
          operationalFreeCashFlow: null,
        });
      });
      dispatch(setDealThesisShowHistoricYears(true));
    } else {
      [
        ...Array(Number(getValues().numberOfHistoricYears) - Number(countOfHistoricYears)).keys(),
      ].map((index) => {
        appendHistoricForecastsField({
          ...lastCaseItemHistoricForecast,
          date: new Date(lastCaseItemHistoricForecast.date)
            .subtractYears(index + 1)
            .toISODateString(),
        });
      });
    }
  };

  const submitData = async () => {
    const {
      caseId: formCaseId,
      historicalYear,
      keyFinancialMetrics,
      activeMetric,
      historicForecasts,
      netDebtItems,
      valuationDateCashValue,
      ...caseDtoValues
    } = getValues();

    // Prevent race conditions during case switches where blur can submit stale form state.
    if (formCaseId !== caseId) {
      return;
    }

    const targetCaseId = formCaseId ?? caseId;
    const targetCaseData = project.pwermInput.cases.find((item) => item.caseId === targetCaseId);
    const newMetricDefinitions = keyFinancialMetrics.reduce((acc, metric) => {
      acc[metric.metricId] = {
        narrative: metric.narrative,
      };
      return acc;
    }, {} as Record<string, FinancialMetricDefinitionDto>);

    const newValuationDateMetrics = keyFinancialMetrics.reduce((acc, metric) => {
      acc[metric.metricId] = metric.forecast[0] ?? 0;
      return acc;
    }, {} as Record<string, number>);

    const updatedHistoricYears = cloneToMutableData(historicForecasts);

    if (!isReadOnly) {
      const formNetDebtById = new Map(netDebtItems.map((item) => [item.netDebtItemId, item]));
      const formNetDebtIds = new Set(netDebtItems.map((item) => item.netDebtItemId));
      const existingBridgeItemIds = new Set(
        project.equityBridge.netDebtItems.map((item) => item.netDebtItemId)
      );
      const existingCommonIds = new Set(project.equityBridge.commonNetDebtItemIds);

      const newCaseSpecificIds = netDebtItems
        .filter((item) => !existingBridgeItemIds.has(item.netDebtItemId))
        .map((item) => item.netDebtItemId);

      const promotedToCommonIds = netDebtItems
        .filter(
          (item) => item.historicalValue != null && !existingCommonIds.has(item.netDebtItemId)
        )
        .map((item) => item.netDebtItemId);

      const deletedCommonIds = project.equityBridge.commonNetDebtItemIds.filter(
        (id) => !formNetDebtIds.has(id)
      );

      const finalCommonNetDebtItemIds = Array.from(
        new Set([...project.equityBridge.commonNetDebtItemIds, ...promotedToCommonIds])
      ).filter((id) => !deletedCommonIds.includes(id));

      const targetCaseExistingNetDebtIds = new Set(
        (targetCaseData?.forecasts ?? [])
          .flatMap((forecast) => forecast.netDebtValues)
          .map((item) => item.netDebtItemId)
      );

      const deletedFromTargetCaseIds = Array.from(targetCaseExistingNetDebtIds).filter(
        (id) => !formNetDebtIds.has(id)
      );

      const updatedCasesData = project.pwermInput.cases.map((caseItem) => {
        if (caseItem.caseId !== targetCaseId) {
          return {
            ...caseItem,
            forecasts: caseItem.forecasts.map((forecast) => {
              const metricValues = cloneToMutableData(forecast.metricValues) || {};
              const metricIds = Object.keys(metricValues);
              const missingMetrics = keyFinancialMetrics.filter(
                (metric) => !metricIds.includes(metric.metricId)
              );
              // Add new metrics to cases
              const addMetricValues = missingMetrics.reduce((acc, metric) => {
                acc[metric.metricId] = 0;
                return acc;
              }, {} as Record<string, number>);

              // remove case's metrics that were deleted from the metric definitions
              Object.keys(metricValues).forEach((metricId) => {
                if (!keyFinancialMetrics.some((metric) => metric.metricId === metricId)) {
                  delete metricValues[metricId];
                }
              });
              return {
                ...forecast,
                metricValues: {
                  ...metricValues,
                  ...addMetricValues,
                },
                netDebtValues: forecast.netDebtValues.filter(
                  (value) => !deletedCommonIds.includes(value.netDebtItemId)
                ),
              };
            }),
          };
        }

        const forecasts = caseDtoValues.forecasts.map((forecast, index) => {
          const existingForecast = caseItem.forecasts[index];
          const existingValues = existingForecast?.netDebtValues ?? [];
          const formValuesById = new Map(
            (forecast.netDebtValues ?? []).map((value) => [value.netDebtItemId, value.value])
          );

          const retainedAndUpdatedValues = existingValues
            .filter(
              (value) =>
                !deletedFromTargetCaseIds.includes(value.netDebtItemId) &&
                !deletedCommonIds.includes(value.netDebtItemId)
            )
            .map((value) => ({
              ...value,
              value: formValuesById.has(value.netDebtItemId)
                ? formValuesById.get(value.netDebtItemId) ?? null
                : value.value,
            }));

          const newCaseSpecificValues = newCaseSpecificIds
            .filter(
              (id) =>
                !retainedAndUpdatedValues.some(
                  (existingValue) => existingValue.netDebtItemId === id
                )
            )
            .map((id) => ({
              netDebtItemId: id,
              value: formValuesById.get(id) ?? null,
            }));

          const newCommonValuesFromCurrentCaseEdits = Array.from(formValuesById.entries())
            .filter(
              ([netDebtItemId, value]) =>
                project.equityBridge.commonNetDebtItemIds.includes(netDebtItemId) &&
                !existingValues.some(
                  (existingValue) => existingValue.netDebtItemId === netDebtItemId
                ) &&
                value != null
            )
            .map(([netDebtItemId, value]) => ({
              netDebtItemId,
              value,
            }));

          return {
            ...forecast,
            metricValues: keyFinancialMetrics.reduce((acc, metric) => {
              acc[metric.metricId] = metric.forecast[index + 1] ?? 0;
              return acc;
            }, {} as Record<string, number>),
            netDebtValues: [
              ...retainedAndUpdatedValues,
              ...newCaseSpecificValues,
              ...newCommonValuesFromCurrentCaseEdits,
            ],
          };
        });
        return {
          ...caseItem,
          ...cloneToMutableData(caseDtoValues),
          caseId: caseItem.caseId,
          forecasts: cloneToMutableData(forecasts),
        };
      });

      const dividendSetId = targetCaseData?.dividendSetId;
      let forecastDividendSet;
      if (dividendSetId != null) {
        forecastDividendSet = cloneToMutableData(
          capitalStructure.forecastDividendSets[dividendSetId]
        );
      }
      if (forecastDividendSet) {
        const numberOfForecastYears = getValues().numberOfForecastYears;

        Object.values(forecastDividendSet.instruments).forEach((instrument) => {
          const existingDates = Object.keys(instrument.dates);
          const lastDate =
            existingDates.length > 0 ? existingDates[existingDates.length - 1] : null;

          if (numberOfForecastYears > existingDates.length && lastDate) {
            for (let i = existingDates.length; i < numberOfForecastYears; i++) {
              const newYearDate = new Date(lastDate);
              newYearDate.setFullYear(newYearDate.getFullYear() + (i - existingDates.length + 1));
              const isoDate = newYearDate.toISOString().slice(0, 10);

              instrument.dates[isoDate] = { totalDividendValue: null };
            }
          }
        });
      }

      const updatedEquityBridge = {
        ...project.equityBridge,
        netDebtItems: [
          ...project.equityBridge.netDebtItems
            .filter((item) => !deletedCommonIds.includes(item.netDebtItemId))
            .map((item) => ({
              netDebtItemId: item.netDebtItemId,
              narrative: formNetDebtById.get(item.netDebtItemId)?.narrative ?? item.narrative,
            })),
          ...netDebtItems
            .filter((item) => !existingBridgeItemIds.has(item.netDebtItemId))
            .map((item) => ({
              netDebtItemId: item.netDebtItemId,
              narrative: item.narrative,
            })),
        ],
        commonNetDebtItemIds: finalCommonNetDebtItemIds,
        columns: project.equityBridge.columns.map((column) => {
          const retainedEntries = Object.entries(column.netDebtValues)
            .filter(([netDebtItemId]) => !deletedCommonIds.includes(netDebtItemId))
            .map(([netDebtItemId, value]) => {
              if (column.role !== EquityBridgeColumnRole.Valuation) {
                return [netDebtItemId, value] as const;
              }

              const formItem = formNetDebtById.get(netDebtItemId);
              const shouldUpdateValuationValue =
                formItem != null &&
                (finalCommonNetDebtItemIds.includes(netDebtItemId) ||
                  formItem.historicalValue != null);

              return [
                netDebtItemId,
                shouldUpdateValuationValue ? formItem.historicalValue ?? null : value,
              ] as const;
            });

          const newValuationEntries =
            column.role === EquityBridgeColumnRole.Valuation
              ? netDebtItems
                  .filter(
                    (item) =>
                      !Object.prototype.hasOwnProperty.call(
                        column.netDebtValues,
                        item.netDebtItemId
                      ) &&
                      (finalCommonNetDebtItemIds.includes(item.netDebtItemId) ||
                        item.historicalValue != null)
                  )
                  .map((item) => [item.netDebtItemId, item.historicalValue ?? null] as const)
              : [];

          return {
            ...column,
            cashValue:
              column.role === EquityBridgeColumnRole.Valuation
                ? valuationDateCashValue ?? null
                : column.cashValue,
            netDebtValues: Object.fromEntries([...retainedEntries, ...newValuationEntries]),
          };
        }),
      };

      await dispatch(
        projectActions.updateProjectDraft({
          project: {
            ...project,
            pwermInput: {
              ...project.pwermInput,
              cases: [...updatedCasesData],
              valuationDateNarrative: historicalYear,
              metricDefinitions: {
                ...newMetricDefinitions,
              },
              valuationDateMetrics: {
                ...newValuationDateMetrics,
              },
              valueDriverActiveMetric: activeMetric,
              historicForecasts: updatedHistoricYears,
            },
            equityBridge: updatedEquityBridge,
            capitalStructures: {
              ...capitalStructures,
              [capitalStructureKey]: {
                ...capitalStructure,
                forecastDividendSets: {
                  ...capitalStructure.forecastDividendSets,
                  ...(dividendSetId && forecastDividendSet
                    ? { [dividendSetId]: forecastDividendSet }
                    : {}),
                },
              },
            },
          },
        })
      ).unwrap();
    }
  };

  const submitValuationDateCashValue = async () => {
    const { caseId: formCaseId, valuationDateCashValue } = getValues();

    // Ignore stale blur submits during case switches.
    if (formCaseId !== caseId || isReadOnly) {
      return;
    }

    const updatedEquityBridge = {
      ...project.equityBridge,
      columns: project.equityBridge.columns.map((column) => ({
        ...column,
        cashValue:
          column.role === EquityBridgeColumnRole.Valuation
            ? valuationDateCashValue ?? null
            : column.cashValue,
      })),
    };

    await dispatch(
      projectActions.updateProjectDraft({
        project: {
          ...project,
          equityBridge: updatedEquityBridge,
        },
      })
    ).unwrap();
  };

  const submitValuationDateNetDebtValues = async () => {
    const { caseId: formCaseId, netDebtItems } = getValues();

    // Ignore stale blur submits during case switches.
    if (formCaseId !== caseId || isReadOnly) {
      return;
    }

    const formNetDebtById = new Map(netDebtItems.map((item) => [item.netDebtItemId, item]));
    const existingCommonIds = new Set(project.equityBridge.commonNetDebtItemIds);
    const promotedToCommonIds = netDebtItems
      .filter((item) => item.historicalValue != null && !existingCommonIds.has(item.netDebtItemId))
      .map((item) => item.netDebtItemId);

    const finalCommonNetDebtItemIds = Array.from(
      new Set([...project.equityBridge.commonNetDebtItemIds, ...promotedToCommonIds])
    );

    const updatedEquityBridge = {
      ...project.equityBridge,
      netDebtItems: project.equityBridge.netDebtItems.map((item) => ({
        netDebtItemId: item.netDebtItemId,
        narrative: formNetDebtById.get(item.netDebtItemId)?.narrative ?? item.narrative,
      })),
      commonNetDebtItemIds: finalCommonNetDebtItemIds,
      columns: project.equityBridge.columns.map((column) => {
        if (column.role !== EquityBridgeColumnRole.Valuation) {
          return column;
        }

        const valuationEntries = Object.fromEntries(
          netDebtItems
            .filter((item) => finalCommonNetDebtItemIds.includes(item.netDebtItemId))
            .map((item) => [item.netDebtItemId, item.historicalValue ?? null])
        );

        return {
          ...column,
          netDebtValues: {
            ...column.netDebtValues,
            ...valuationEntries,
          },
        };
      }),
    };

    await dispatch(
      projectActions.updateProjectDraft({
        project: {
          ...project,
          equityBridge: updatedEquityBridge,
        },
      })
    ).unwrap();
  };

  const handleFormBlur = (event: FocusEvent<HTMLFormElement>) => {
    const target = event.target;

    if (target instanceof HTMLInputElement && target.name === 'valuationDateCashValue') {
      void submitValuationDateCashValue();
      return;
    }

    if (
      target instanceof HTMLInputElement &&
      /^netDebtItems\.\d+\.historicalValue$/.test(target.name)
    ) {
      void submitValuationDateNetDebtValues();
      return;
    }

    void handleSubmit(submitData, submitData)(event);
  };

  const handleCaseRemoval = async (caseId: string | undefined) => {
    // although we block the user from deleting the actively selected OPM case in PWERM&OPM,
    // this limitation is not appropriate for PWERM, hence the handling of this scenario

    const opmSelectedCaseId =
      caseId === project.opmInput.selectedCaseId
        ? project.pwermInput.cases.find((i) => i.caseId !== caseId)?.caseId ??
          project.opmInput.selectedCaseId
        : project.opmInput.selectedCaseId;

    const dividendSetToDelete = project.pwermInput.cases.find(
      (i) => i.caseId === caseId
    )?.dividendSetId;

    const updatedCapitalStructures = cloneToMutableData(project.capitalStructures);

    if (dividendSetToDelete) {
      delete updatedCapitalStructures[capitalStructureKey].forecastDividendSets[
        dividendSetToDelete
      ];
    }

    const updatedCases = project.pwermInput.cases.filter((i) => i.caseId !== caseId);

    const updatedProject = {
      ...project,
      capitalStructures: updatedCapitalStructures,
      pwermInput: {
        ...project.pwermInput,
        cases: updatedCases,
      },
      opmInput: {
        ...project.opmInput,
        selectedCaseId: opmSelectedCaseId,
      },
    };

    await dispatch(
      projectActions.updateProjectDraft({
        project: updatedProject,
      })
    ).unwrap();

    // Force recalculation of buildStructures for ERF controls
    await dispatch(capitalStructureActions.calculateBuildStructure({ ignoreErrors: true }));

    setIsDeleteCaseModalVisible(false);
    const index = project.pwermInput.cases.findIndex((item) => item.caseId === caseId);
    navigate(
      generatePath('../:caseId', {
        caseId: project.pwermInput.cases[index === 0 ? index + 1 : index - 1].caseId,
      })
    );
  };

  // the ternary operator is used to safely handle the scenario where a case is added, and attempts to render, but the results are not yet available
  const numberOfResultsForecastYears = results.cases[caseId!]
    ? Object.keys(results.cases[caseId!].forecastDates).length
    : 0;
  const numberOfResultsHistoricForecastYears = results.cases[caseId!]
    ? Object.keys(results.cases[caseId!].historicDates?.historicForecastYears ?? {}).length
    : 0;

  useEffect(() => {
    // if the case is not yet available in the results, return
    if (results.cases[caseId!] === undefined) {
      return;
    }

    // resolve concurrency issue
    setTimeout(() => trigger(), 0);
    // numberOfResultsForecastYears and numberOfResultsHistoricForecastYears are included in the dependency array to ensure that the form is revalidated a change to either is made
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger, caseId, numberOfResultsForecastYears, numberOfResultsHistoricForecastYears]);

  // return null if results.cases[caseId!] is undefined, which can happen if a case is added, and attempts to render, but the results are not yet available
  if (results.cases[caseId!] === undefined || caseIdIsInvalid) {
    return null;
  }

  if (project.details.calcMethod === CalcMethod.OPM) {
    const newPath = generatePath(`../../${RouteConstants.ProjectDetails}`);
    return <Navigate to={newPath} replace />;
  }

  if (!isPwermInputsValid) {
    const newPath = generatePath(`../../${RouteConstants.ProjectDetails}`);
    return <Navigate to={newPath} replace />;
  }

  const isSingleCase = project.pwermInput.cases.length === 1;

  const isCaseUsedForCurrentOpmResults =
    project.details.calcMethod === enumKeyByValue(CalcMethod, CalcMethod.PWERM_AND_OPM) &&
    opmResultsInputSummary?.selectedCaseId === caseId;

  const isCaseSelectedInOpm =
    project.details.calcMethod === enumKeyByValue(CalcMethod, CalcMethod.PWERM_AND_OPM) &&
    project.opmInput.selectedCaseId === caseId;

  const shouldDeleteButtonBeDisabled =
    isSingleCase || isCaseSelectedInOpm || isCaseUsedForCurrentOpmResults;

  const tooltipMessages = [];

  if (isSingleCase) {
    tooltipMessages.push(l('_AtLeastOneCaseIsRequired'));
  }
  if (isCaseSelectedInOpm) {
    tooltipMessages.push(l('_CaseIsSelectedInOpm'));
  }
  if (isCaseUsedForCurrentOpmResults) {
    tooltipMessages.push(l('_CaseIsDependencyForOpm'));
  }

  const tooltipContent =
    tooltipMessages.length > 0 ? (
      <ul className={styles['delete-case-tooltip-list']}>
        {tooltipMessages.map((message, index) => (
          <li key={index} className={styles['delete-case-tooltip-item']}>
            {message}
          </li>
        ))}
      </ul>
    ) : undefined;

  return (
    <FormProvider {...formMethods}>
      <form onBlur={handleFormBlur} className={styles[`table-controls__form`]}>
        {caseProbabilitiesTotal !== 100 && (
          <div className={styles['notification']}>
            <NotificationMessage>{probabilitiesTotalWarningMessage}</NotificationMessage>
          </div>
        )}
        {!isStructureTheSame && (
          <div className={styles['notification']}>
            <NotificationMessage type={NotificationMessageType.warning}>
              {caseStructuresDiffersWarningMessage}
            </NotificationMessage>
          </div>
        )}
        <Card className={styles[PARENT_CLASSNAME]}>
          <FormField
            name="narrative"
            label={l('_CaseNarrativeTitle')}
            required
            rules={{
              ...getRequiredValidator(),
            }}
            isInputMasked={false}
            fieldValueMask={swapMaskForValueDisplay}
            isGapless
          />
          <FormField
            name="probability"
            label={l('_CaseProbabilityTitle')}
            required
            rules={{
              ...getRequiredValidator(),
              ...percentageValidator,
            }}
            {...percentageFieldFormattingProps}
            isGapless
          />
          <FormSelect
            name="numberOfForecastYears"
            label={l('_NumberOfForecastYears')}
            onBlur={() => handleForecastYearQuantityChange()}
            required
            rules={{
              ...getRequiredValidator(),
            }}
            options={numberOfForecastYearsOptions}
            isGapless
          />
          {numberOfHistoricYearsOptions.length > 1 && isHistoricInputsEnabled && (
            <FormSelect
              name="numberOfHistoricYears"
              label={l('_NumberOfHistoricYears')}
              onBlur={() => handleHistoricForecastYearQuantityChange()}
              options={numberOfHistoricYearsOptions}
              isGapless
            />
          )}
          <div className={styles[`${PARENT_CLASSNAME}__action`]}>
            <Tooltip
              content={shouldDeleteButtonBeDisabled ? tooltipContent : undefined}
              useFixedPositioning
              shouldTooltipRender={shouldDeleteButtonBeDisabled}
              shouldVerticallyCenterChildElement
              placement={TooltipPlacement.Left}>
              <div>
                <Button
                  appearance={ButtonAppearance.DEFAULT_SECONDARY}
                  startIcon={<SvgTrash />}
                  disabled={shouldDeleteButtonBeDisabled}
                  onClick={() => setIsDeleteCaseModalVisible(true)}>
                  {l('_DeleteCaseTitle')}
                </Button>
              </div>
            </Tooltip>
          </div>
          <BaseModal
            size={ModalSize.Small}
            title={l('_DeleteActiveCase', { case: activeCase })}
            buttonConfirmLabel={l('_DeleteCaseTitle')}
            buttonCancelLabel={l('_Cancel')}
            onCloseButtonClick={() => setIsDeleteCaseModalVisible(false)}
            onConfirm={() => handleCaseRemoval(caseId)}
            onCancel={() => setIsDeleteCaseModalVisible(false)}
            isOpen={isDeleteCaseModalVisible}>
            {l('_ThisWillDeleteActiveCaseAndAnyDataYouveEntered', { case: activeCase })}
          </BaseModal>
          <ReduceYearsModal
            onCloseButtonClick={() => revertForecastYears()}
            onCancel={() => revertForecastYears()}
            onConfirm={() => removeForecastYears()}
            isOpen={isForecastYearRemovalModalVisible}
            type={ReduceYearsType.Forecast}
          />
          <ReduceYearsModal
            onCloseButtonClick={() => revertHistoricForecastYears()}
            onCancel={() => revertHistoricForecastYears()}
            onConfirm={() => removeHistoricForecastYears()}
            isOpen={isHistoricForecastYearRemovalModalVisible}
            type={ReduceYearsType.Historic}
          />
        </Card>
      </form>
      <form onBlur={handleFormBlur}>
        <DealThesisCaseTable2 handleDataSubmit={submitData} />
      </form>
    </FormProvider>
  );
};

export const DealThesisNavigation: FC = (): JSX.Element => {
  const { cases } = useAppSelector((state) => state.project.projectDraft).pwermInput;
  const storedDealThesisCaseId = useAppSelector(
    (state) => state.uiValues.userSelections.dealThesis.selectedTab
  );
  const isStoredDealThesisCaseIdValid =
    storedDealThesisCaseId && cases.some((caseItem) => caseItem.caseId === storedDealThesisCaseId);
  const { generatePath } = usePwcNavigate();

  return (
    <PwcNavigate
      route={generatePath(
        ':caseId',
        isStoredDealThesisCaseIdValid
          ? { caseId: storedDealThesisCaseId }
          : { caseId: cases[0].caseId }
      )}
    />
  );
};
